import { useAnimal, useAnimals } from '@/api/hooks';
import { AnimalChangesPanel } from '@/components/animals/AnimalChangesPanel';
import { AnimalDairyPanel } from '@/components/animals/AnimalDairyPanel';
import { AnimalDetailTabs, type AnimalDetailTabId } from '@/components/animals/AnimalDetailTabs';
import { AnimalFichaForm } from '@/components/animals/AnimalFichaForm';
import { AnimalMediaGrid } from '@/components/animals/AnimalMediaGrid';
import { AnimalPedigreePanel } from '@/components/animals/AnimalPedigreePanel';
import { AnimalRowActionsMenu } from '@/components/animals/AnimalRowActionsMenu';
import { AnimalTimeline } from '@/components/animals/AnimalTimeline';
import { AnimalZootecnicosPanel } from '@/components/animals/AnimalZootecnicosPanel';
import { AssignLotModal } from '@/components/animals/AssignLotModal';
import { DeleteAnimalModal } from '@/components/animals/DeleteAnimalModal';
import { EventFormModal, REPRODUCTION_EVENT_TYPES } from '@/components/events/EventFormModal';
import { MovementFormModal } from '@/components/movements/MovementFormModal';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { useAuth } from '@/contexts/AuthContext';
import { animalToSummary } from '@/lib/pedigree';
import { canManage } from '@/lib/rbac';
import { ArrowLeft } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

export function AnimalDetailPage() {
  const { id } = useParams();
  const animalId = Number(id);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: animal, isLoading } = useAnimal(animalId);
  const { data: herd } = useAnimals({
    property_id: animal?.property_id,
    per_page: 500,
    include_archive: true,
  });
  const [tab, setTab] = useState<AnimalDetailTabId>('ficha');
  const [activeModal, setActiveModal] = useState<
    'event' | 'movement' | 'reproduction' | 'assignLot' | 'delete' | null
  >(null);

  const summary = useMemo(() => (animal ? animalToSummary(animal) : null), [animal]);
  const canDelete = !!user && canManage(user.role);
  const showDairy = animal?.sex === 'female';

  if (isLoading) return <div className="h-48 rounded-2xl bg-manejai-sand/40 animate-pulse" />;
  if (!animal || !summary) return <p>Animal não encontrado.</p>;

  return (
    <div>
      <Link
        to="/animais"
        className="mb-4 inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 -ml-2 text-sm text-manejai-bark/70 transition hover:bg-manejai-sand/50 hover:text-manejai-forest"
      >
        <ArrowLeft className="h-4 w-4" />
        Voltar
      </Link>

      <Card>
        <CardHeader className="border-b-0 pb-0 pt-4">
          <AnimalDetailTabs
            tab={tab}
            onChange={setTab}
            showDairy={showDairy}
            trailing={
              <AnimalRowActionsMenu
                animal={summary}
                onCreateEvent={() => setActiveModal('event')}
                onCreateMovement={() => setActiveModal('movement')}
                onCreateReproduction={() => setActiveModal('reproduction')}
                onAssignLot={() => setActiveModal('assignLot')}
                onEditFicha={() => setTab('ficha')}
                onDelete={canDelete ? () => setActiveModal('delete') : undefined}
                canDelete={canDelete}
              />
            }
          />
        </CardHeader>
        <CardBody>
          {tab === 'ficha' && <AnimalFichaForm key={animal.id} animal={animal} />}
          {tab === 'media' && <AnimalMediaGrid animalId={animalId} canDelete />}
          {tab === 'timeline' && <AnimalTimeline animalId={animalId} view="timeline" />}
          {tab === 'leite' && showDairy && <AnimalDairyPanel animalId={animalId} />}
          {tab === 'zootecnia' && <AnimalZootecnicosPanel animalId={animalId} />}
          {tab === 'afiliacao' && <AnimalPedigreePanel animalId={animalId} />}
          {tab === 'alteracoes' && <AnimalChangesPanel animalId={animalId} />}
        </CardBody>
      </Card>

      <EventFormModal
        open={activeModal === 'event'}
        onClose={() => setActiveModal(null)}
        propertyId={animal.property_id}
        presetAnimal={summary}
      />
      <MovementFormModal
        open={activeModal === 'movement'}
        onClose={() => setActiveModal(null)}
        propertyId={animal.property_id}
        presetAnimalId={animal.id}
      />
      <EventFormModal
        open={activeModal === 'reproduction'}
        onClose={() => setActiveModal(null)}
        propertyId={animal.property_id}
        presetAnimal={summary}
        allowedTypes={REPRODUCTION_EVENT_TYPES}
        title="Registrar reprodução"
      />
      <AssignLotModal
        open={activeModal === 'assignLot'}
        onClose={() => setActiveModal(null)}
        propertyId={animal.property_id}
        animal={summary}
      />
      <DeleteAnimalModal
        animal={activeModal === 'delete' ? summary : null}
        propertyAnimals={herd?.animals}
        open={activeModal === 'delete'}
        onClose={() => setActiveModal(null)}
        onDeleted={() => navigate('/animais', { replace: true, state: { message: 'Animal excluído.' } })}
      />
    </div>
  );
}
